import { ThunkAction } from 'redux-thunk'

import { Action, LoadedCase, LoadedCaseValues } from './types'
import { DefaultValues } from '../Components/Workspace/Calculation/defaultValuesQuery'
import { uuidv4 } from '../util/uuid'
import { ProjectType } from '../Components/Projects/projectQueries'
import {
  EditableLoadCase,
  EditableBeam,
  EditableSlab,
  EditableLoad
} from '../reducers/project'
import { Store } from '../reducers'
import { convertDefaultLoadNames } from '../location_changes/conversionHelper'

// Promise resolves to null (success) or error (string). Most of the time you should
// not use the return value and just rely on the Redux state. This return value is
// mostly a convenience for project loader.
export type ThunkedAction = ThunkAction<
  Promise<string | null>,
  Store.Complete,
  {}
>

export type ProjectCaseType = {
  id: string
  projectId: string
  thumbnail: string | null // URL of the autogenerated case thumbnail
  name: string
  description: string
  lastReport: string | null // URL of the last generated report, if available
  lastResults: string // ID(?) of the last result set
  spaceType: string // space type and parameters used by this case. Parameters is a stringified JSON object
  parameters: string
  data: string // User data
}

/*
    Load / init actions
*/

function parseDefaults(defaultValues: DefaultValues): LoadedCaseValues {
  const beams = JSON.parse(defaultValues.beams)
  const slabs = JSON.parse(defaultValues.slabs)
  const loads = convertDefaultLoadNames(JSON.parse(defaultValues.loads))

  return {
    beams: beams.map(beam => ({ id: uuidv4(), ...beam })),
    slabs: slabs.map(slab => ({ id: uuidv4(), ...slab })),
    loadCases: loads.map(load => ({ id: uuidv4(), ...load }))
  }
}

export function loadDefaultValues(
  spaceType: string,
  defaultValues: DefaultValues
): Action {
  // TODO: Handle errors here
  return {
    type: 'LOAD_DEFAULT_VALUES',
    payload: { spaceType, values: parseDefaults(defaultValues) }
  }
}

export function initializeProject(defaultValues: DefaultValues): ThunkedAction {
  return dispatch => {
    let defaults
    try {
      defaults = parseDefaults(defaultValues)
    } catch (e) {
      dispatch({
        type: 'LOAD_PROJECT',
        error: true,
        payload: 'Initialize failed'
      })
      return Promise.reject('Initialize failed')
    }

    dispatch({
      type: 'INITIALIZE_PROJECT',
      payload: defaults
    })
    return Promise.resolve(null)
  }
}

export function unloadProject(): Action {
  return { type: 'UNLOAD_PROJECT' }
}

type DefaultParams = {
  fireResistance: string
}

function parseCase(defaults: DefaultParams) {
  return function(kase: ProjectCaseType): LoadedCase {
    const data = JSON.parse(kase.data)

    // XXX: Once we have a server-side "validation" and correction mechanism for filling
    // in missing data, we can remove this but for now we have to ensure that we have a
    // fire resistance value
    const beams = data.beams.map(beam => ({
      ...beam,
      fireResistance: beam.fireResistance || defaults.fireResistance
    }))

    const slabs = data.slabs
    const loadCases = data.loads
    const fireResistance = JSON.parse(kase.parameters).fireResistance

    return {
      id: kase.id,
      name: kase.name,
      description: kase.description,
      spaceType: kase.spaceType,
      beams,
      slabs,
      loadCases,
      fireResistance,
      activeItem: data.activeItem,
      touchedCategories: data.touchedCategories || ['location', 'spacetype']
    }
  }
}

export function loadProject(project: ProjectType): ThunkedAction {
  return dispatch => {
    try {
      const parameters = JSON.parse(project.parameters)

      const cases: LoadedCase[] = project.cases.map(
        parseCase({ fireResistance: parameters.fireResistance })
      )

      dispatch({
        type: 'LOAD_PROJECT',
        error: false,
        payload: {
          projectId: project.id,
          name: project.name,
          location: project.location,
          spaceType: project.spaceType,
          defaultConcreteType: parameters.beamConcreteGrade,
          defaultFireResistance: parameters.fireResistance,
          cases,
          activeCase: project.activeCase
        }
      })
      return Promise.resolve(null)
    } catch (e) {
      console.warn('Error loading project', e)
      dispatch({ type: 'LOAD_PROJECT', error: true, payload: e })
      return Promise.reject('Error loading project: ' + e.message)
    }
  }
}

/* Signal that project load/init failed */
export function loadProjectError(error: string): Action {
  return { type: 'LOAD_PROJECT', error: true, payload: new Error(error) }
}

/* Reset project to 'non-dirty' state */
export function resetProjectDirty(): Action {
  return { type: 'RESET_PROJECT_DIRTY' }
}

export function selectCountry(country: string): Action {
  return {
    type: 'SELECT_COUNTRY',
    payload: country
  }
}

export function selectSpaceType(id: string): Action {
  return {
    type: 'SELECT_SPACE_TYPE',
    payload: id
  }
}

/*
    Workspace actions
*/

// Thunk because we need getState() for the default concrete grade
export function createCase(
  defaults: DefaultValues
): ThunkAction<void, Store.Complete, {}> {
  /* Once we have a better understanding of what values have project defaults and
     which don't, we should make this less ugly. Right now new case mostly uses
     defaults but we need to override the beam's concrete grade with the default.

    This should actually be an issue for fire resistance as well but we don't
    currently have fire resistance in beam properties so we dodget that bullet.

    The "correct" approach probably involves maintaining defaults but then
    manually constructing the beams for new cases (and when creating a new project). */
  return (dispatch, getState) => {
    let values = parseDefaults(defaults)
    values.beams[0].concreteGrade = getState().project.defaultConcreteGrade

    return dispatch({
      type: 'CREATE_CASE',
      payload: {
        id: uuidv4(),
        name: 'New case',
        ...values
      }
    })
  }
}

export function deleteCase(caseId: string): Action {
  return { type: 'DELETE_CASE', payload: caseId }
}

export function changeCase(caseId: string): Action {
  return { type: 'SELECT_CASE', payload: caseId }
}

export function selectCaseItem(itemId: string): Action {
  return { type: 'CASE_SELECT_ITEM', payload: itemId }
}

export function selectNextCaseItem(): Action {
  return { type: 'CASE_SELECT_NEXT_ITEM' }
}

export function selectFirstBeam(): Action {
  return { type: 'CASE_SELECT_FIRST_BEAM' }
}

export function selectFirstSlab(): Action {
  return { type: 'CASE_SELECT_FIRST_SLAB' }
}

export function selectFirstLoadCase(): Action {
  return { type: 'CASE_SELECT_FIRST_LOAD_CASE' }
}

export function updateLoadCase(loadCase: EditableLoadCase): Action {
  return { type: 'UPDATE_LOAD_CASE', payload: loadCase }
}

export function deleteLoadCase(loadCaseId: string): Action {
  return { type: 'DELETE_LOAD_CASE', payload: loadCaseId }
}

export function updateLoad(
  loadCaseId: string,
  index: number,
  load: EditableLoad
): Action {
  return { type: 'UPDATE_LOAD', payload: { loadCaseId, index, load } }
}

export function deleteLoad(loadCaseId: string, index: number): Action {
  return { type: 'DELETE_LOAD', payload: { loadCaseId, index } }
}

export function updateBeam(beam: EditableBeam): Action {
  return { type: 'UPDATE_BEAM', payload: beam }
}

export function toggleOtherSlabs(slabId: string, value: boolean): Action {
  return { type: 'TOGGLE_OTHER_SLABS', payload: { slabId, value } }
}

export function updateSlab(slab: EditableSlab): Action {
  return { type: 'UPDATE_SLAB', payload: slab }
}

export function addLoadCase(): Action {
  return {
    type: 'ADD_LOAD_CASE',
    payload: {
      id: uuidv4(),
      name: 'New load case',
      action: 'Storage area',
      auto: false,
      stage: 'finalComposite',
      loads: [
        { type: 'area', name: 'New load', values: [[0, 0.5]], actsOn: [] }
      ]
    }
  }
}

export function addLoad(loadCaseId: string): Action {
  return { type: 'ADD_LOAD', payload: { loadCaseId } }
}

export function selectLoad(loadCaseId: string, index: number): Action {
  return { type: 'SELECT_LOAD', payload: { loadCaseId, index } }
}

export function saveThumbnail(thumbnailData: string): Action {
  return { type: 'SAVE_TEMP_THUMBNAIL', payload: thumbnailData }
}



// WEBPACK FOOTER //
// ./src/actions/project.ts